import React, { useState, useMemo } from "react";
import {
  useServiceCentersQuery,
  useDeleteServiceCenterMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  CustomerSupportIcon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

// Empty state component for when no service centers are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={CustomerSupportIcon}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_SERVICE_CENTERS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_SERVICE_CENTERS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={CustomerSupportIcon}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_SERVICE_CENTER")}
        </Link>
      )}
    </div>
  );
};

const getSystemFromPath = (pathname) =>
  pathname?.includes("madina") ? "madina" : "makkah";

export const ServiceCenters = () => {
  const location = useLocation();
  const system = getSystemFromPath(location.pathname);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    serviceCenterId: null,
  });
  const { data, isLoading, isFetching } = useServiceCentersQuery(
    { page: currentPage, per_page: 10 },
    { system }
  );
  const serviceCenters = data?.data?.data ?? [];
  const { filteredData, ...restClientSearch } = useClientSearch(serviceCenters);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getLocalizedText } = useLocalizedText();

  const loading = isLoading || isFetching;

  // Delete mutation
  const { mutate: deleteServiceCenter, isPending: isDeleting } =
    useDeleteServiceCenterMutation(undefined, { system });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle delete modal
  const handleDeleteClick = (serviceCenterId) => {
    setDeleteModalState({ isOpen: true, serviceCenterId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, serviceCenterId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.serviceCenterId) {
      deleteServiceCenter(deleteModalState.serviceCenterId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, serviceCenterId: null });
          queryClient.invalidateQueries({
            queryKey: ["service-centers", system],
          });
        },
      });
    }
  };
  // Define columns configuration (nationality column only for Madina)
  const columns = useMemo(
    () => [
      {
        name: t("NAME_AR"),
        key: "name_ar",
        filterable: true,
      },
      {
        name: t("NAME_EN"),
        key: "name_en",
        filterable: true,
      },
      {
        name: t("CODE"),
        key: "code",
        filterable: true,
      },
      // display address data
      {
        name: t("ADDRESS"),
        key: "address",
        filterable: true,
        render: (_, rowData) =>
          rowData?.address ??
          "-",
      },
      {
        name: t("PHONE"),
        key: "phone",
        filterable: true,
        render: (_, rowData) =>
          rowData?.phone_number ??
          "-",
      },
      // display manager data 
      {
        name: t("MANAGER_NAME"),
        key: "manager_name",
        filterable: true,
        render: (_, rowData) =>
          rowData?.manager?.name_ar ??
          rowData?.manager?.name_en ??
          "-",
      },
      {
        name: t("MANAGER_NATIONAL_ID"),
        key: "manager_national_id",
        filterable: true,
        render: (_, rowData) =>
          rowData?.manager?.national_id ??
          "-",
      },
      {
        name: t("MANAGER_PHONE"),
        key: "manager_phone",
        filterable: true,
        render: (_, rowData) =>
          rowData?.manager?.phone ??
          "-",
      },
      ...(system === "madina"
        ? [
            {
              name: t("NATIONALITY"),
              key: "nationality",
              filterable: true,
              render: (_, rowData) =>
                rowData?.nationality
                  ? getLocalizedText(
                      rowData.nationality.nationalityNameAr,
                      rowData.nationality.nationalityNameEn
                    )
                  : "—",
            },
          ]
        : []),
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t, system, getLocalizedText]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`${location.pathname}/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_SERVICE_CENTER")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        {...restClientSearch}
        columns={columns}
        loading={loading}
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_SERVICE_CENTER"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};

